import React, { useMemo } from 'react';
import PropTypes from 'prop-types';

import styled from 'styled-components';
import CardHeaderNav from '@dashboard/components/CardHeaderNav';
import { useAuth } from '@dashboard/context/AuthContext/AuthContext';
import Button from '@dashboard/components/Button';
import { ChevronLeftIcon } from '@heroicons/react/24/outline';
import { ADMIN_ROUTES } from '../adminRoutes';

// Define which routes should appear as tabs
const NAV_PATHS = [
  '/admin/merchants',
  '/admin/merchants-usage',
  '/admin/pages',
  '/admin/payments',
  '/admin/subscriptions',
  '/admin/submissions',
  '/admin/plans',
  '/admin/templates',
  '/admin/template-categories',
  '/admin/help-center-search-intents',
  '/admin/actions',
  '/admin/statistic-logs',
  '/admin/metric-logs',
  '/admin/integration-logs',
  '/admin/calculate-page-metadata',
];

const capitalizeWords = str =>
  str
    .split(' ')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');

const getNavigation = role => {
  return ADMIN_ROUTES.filter(
    route => NAV_PATHS.includes(route.path) && route.roles.includes(role),
  ).map(route => ({
    name: capitalizeWords(
      route.component.displayName || route.path.split('/').pop().replace(/-/g, ' '),
    ),
    href: route.path,
  }));
};

const Container = styled.div`
  padding: 0 1rem;
  @media (max-width: 768px) {
    padding-left: 0;
    max-width: 100%;
  }
`;

const propTypes = {
  children: PropTypes.oneOfType([PropTypes.node, PropTypes.arrayOf(PropTypes.node)]).isRequired,
};

const defaultProps = {};

const AdminLayout = ({ children }) => {
  const { isAdmin, user } = useAuth();

  if (!isAdmin) {
    return 'Authenticating...';
  }

  const tabs = useMemo(() => getNavigation(user.role), [user.role]);

  return (
    <React.Fragment>
      <Container className="my-4">
        <Button to="/dashboard" className="mb-4" appearance="secondary">
          <ChevronLeftIcon className="w-4 h-4 mr-2" />
          Dashboard
        </Button>

        <CardHeaderNav.Container tabs={tabs}>
          {({ currentTab }) =>
            tabs.map(tab => (
              <CardHeaderNav.Tab key={tab.name} tab={tab} current={currentTab.href === tab.href}>
                {tab.name}
              </CardHeaderNav.Tab>
            ))
          }
        </CardHeaderNav.Container>

        <div className="py-4">{children}</div>
      </Container>
    </React.Fragment>
  );
};

AdminLayout.propTypes = propTypes;
AdminLayout.defaultProps = defaultProps;

export default AdminLayout;
